import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";

export const MIN_ANGLE_EDGE = 40 / 180 * Math.PI;
export const MAX_ANGLE_EDGE = 50 / 180 * Math.PI;

const ANGLE_EPSILON = 1e-6;

const CONTACT_RATIO = 0.4;

const EDGE_PAD_HALF_WIDTH = 0.50;
const EDGE_PAD_HALF_HEIGHT = 0.50;

const LOWER_FACE_MIN_DROP = 0.5;
const FACE_PROBE_RATIO = 0.25;

const FACE_AXES = [
  new THREE.Vector3( 1, 0, 0 ), new THREE.Vector3( -1, 0, 0 ),
  new THREE.Vector3( 0, 1, 0 ), new THREE.Vector3( 0, -1, 0 ),
  new THREE.Vector3( 0, 0, 1 ), new THREE.Vector3( 0, 0, -1 ),
];

export interface EulerRotation
{
  x?: number;
  y?: number;
  z?: number;
}

export interface ExtraCollider
{
  desc: RAPIER.ColliderDesc;
  mass_share: number;
}

const TILT_AXIS = new THREE.Vector3( 0, 0, 1 );

function bodyQuat( rotation: EulerRotation ): THREE.Quaternion
{
  return new THREE.Quaternion().setFromEuler( new THREE.Euler(
    rotation.x ?? 0,
    rotation.y ?? 0,
    rotation.z ?? 0,
    "XYZ"
  ) );
}

function surfaceQuat( surface_tilt_rad: number ): THREE.Quaternion
{
  return new THREE.Quaternion().setFromAxisAngle( TILT_AXIS, surface_tilt_rad );
}

function levelQuat( rotation: EulerRotation, surface_tilt_rad: number ): THREE.Quaternion
{
  return bodyQuat( rotation ).invert().multiply( surfaceQuat( surface_tilt_rad ) );
}

function localDown( rotation: EulerRotation, surface_tilt_rad: number ): THREE.Vector3
{
  return new THREE.Vector3( 0, -1, 0 ).applyQuaternion( levelQuat( rotation, surface_tilt_rad ) );
}

function leanFromSurface( rotation: EulerRotation, surface_tilt_rad: number ): number
{
  const up = new THREE.Vector3( 0, 1, 0 ).applyQuaternion( bodyQuat( rotation ) );
  const normal = new THREE.Vector3( 0, 1, 0 ).applyQuaternion( surfaceQuat( surface_tilt_rad ) );

  return up.angleTo( normal );
}

export function isEdgeStanding( rotation: EulerRotation, surface_tilt_rad: number ): boolean
{
  const lean = leanFromSurface( rotation, surface_tilt_rad );

  return lean >= MIN_ANGLE_EDGE - ANGLE_EPSILON && lean <= MAX_ANGLE_EDGE + ANGLE_EPSILON;
}

export function isLeaning( rotation?: EulerRotation ): boolean
{
  return !!( rotation && ( rotation.x || rotation.z ) );
}

export function edgeSupportPad(
  size: THREE.Vector3,
  rotation: EulerRotation,
  surface_tilt_rad = 0
): ExtraCollider[]
{
  const level = levelQuat( rotation, surface_tilt_rad );
  const down = localDown( rotation, surface_tilt_rad );

  const half = size.clone().multiplyScalar( 0.5 );
  const shortest = Math.min( half.x, half.y, half.z );
  const pad_half_w = shortest * EDGE_PAD_HALF_WIDTH;
  const pad_half_h = shortest * EDGE_PAD_HALF_HEIGHT;

  const dominant = Math.max( Math.abs( down.x ), Math.abs( down.y ), Math.abs( down.z ) );
  const lean = ( v: number ) => Math.abs( v ) < dominant * CONTACT_RATIO ? 0 : Math.sign( v );
  const foot = new THREE.Vector3(
    lean( down.x ) * half.x,
    lean( down.y ) * half.y,
    lean( down.z ) * half.z
  ).addScaledVector( down, -pad_half_h );

  return [ {
    desc: RAPIER.ColliderDesc.cuboid( pad_half_w, pad_half_h, pad_half_w )
      .setTranslation( foot.x, foot.y, foot.z )
      .setRotation( { x: level.x, y: level.y, z: level.z, w: level.w } ),
    mass_share: 0,
  } ];
}

function occupiedByOther( world: RAPIER.World, point: THREE.Vector3, own_body?: number ): boolean
{
  let occupied = false;

  world.forEachCollider( other =>
  {
    if ( !occupied && !other.isSensor() && other.parent()?.handle !== own_body && other.containsPoint( point ) )
      occupied = true;
  } );

  return occupied;
}

export function restsOnFaces( collider: RAPIER.Collider, world: RAPIER.World ): boolean
{
  if ( collider.shapeType() !== RAPIER.ShapeType.Cuboid )
    return false;

  const half = collider.halfExtents();
  const at = collider.translation();
  const turn = collider.rotation();
  const center = new THREE.Vector3( at.x, at.y, at.z );
  const rotation = new THREE.Quaternion( turn.x, turn.y, turn.z, turn.w );
  const reach = Math.min( half.x, half.y, half.z ) * FACE_PROBE_RATIO;
  const own_body = collider.parent()?.handle;

  const lower_faces = FACE_AXES
    .map( axis => ( { axis, normal: axis.clone().applyQuaternion( rotation ) } ) )
    .filter( face => face.normal.y <= -LOWER_FACE_MIN_DROP );

  return lower_faces.length > 0 && lower_faces.every( ( { axis, normal } ) =>
  {
    const depth = Math.abs( axis.x ) * half.x + Math.abs( axis.y ) * half.y + Math.abs( axis.z ) * half.z;
    return occupiedByOther( world, center.clone().addScaledVector( normal, depth + reach ), own_body );
  } );
}

export function edgeSupportFor(
  mesh: THREE.Object3D,
  rotation: EulerRotation | undefined,
  surface_tilt_rad: number
): ExtraCollider[]
{
  if ( !isLeaning( rotation ) || !isEdgeStanding( rotation!, surface_tilt_rad ) )
    return [];

  return edgeSupportPad(
    new THREE.Box3().setFromObject( mesh ).getSize( new THREE.Vector3() ),
    rotation!,
    surface_tilt_rad
  );
}
